/**
 * Stan grup reguł filtrów (IFilterRow[][]) oraz akcje: dodawanie/usuwanie reguł,
 * zastosowanie filtra z kolumny (ColumnFilterPopper), budowanie płaskiej listy do Redux/API.
 */
import { useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'store';
import { reduxActions } from 'store';
import type {
  IWiseTableAdvancedFilter,
  TWiseTableAdvancedFilterOperator,
  IFilterRow
} from 'components/wise/types';
import { getEmptyFilterRow } from 'components/wise/WiseTable/constants';
import { buildFiltersFromGroups } from 'components/wise/WiseTable/utils/filterGroups';

export interface UseFilterRulesParams {
  savedFilters: IWiseTableAdvancedFilter[];
  isDynamicUI: boolean;
  pageSymbol?: string;
  componentSymbol: string;
}

export function useFilterRules({
  savedFilters,
  isDynamicUI,
  pageSymbol,
  componentSymbol
}: UseFilterRulesParams) {
  const dispatch = useDispatch();
  const [filterRuleGroups, setFilterRuleGroups] = useState<IFilterRow[][]>([[getEmptyFilterRow()]]);

  const addRule = useCallback(() => {
    setFilterRuleGroups((prev) => [...prev, [getEmptyFilterRow()]]);
  }, []);

  const applyColumnFilter = useCallback(
    (
      columnId: string,
      rows: Array<{
        operator: TWiseTableAdvancedFilterOperator | null;
        value: string;
        connectorAfter?: 'and' | 'or';
      }>
    ) => {
      const newGroup: IFilterRow[] =
        rows.length > 0
          ? rows.map((r, i) => ({
              ...getEmptyFilterRow(),
              columnFieldSymbol: columnId,
              operator: r.operator,
              value: r.value,
              connectorAfter: i < rows.length - 1 ? (r.connectorAfter ?? 'and') : undefined
            }))
          : [];

      let nextForRedux: IFilterRow[][] = [];
      setFilterRuleGroups((prev) => {
        const withoutColumn = prev.filter(
          (g) => !g.length || g[0].columnFieldSymbol !== columnId
        );
        const next =
          newGroup.length > 0 ? [...withoutColumn, newGroup] : withoutColumn;
        nextForRedux = next.length > 0 ? next : [[getEmptyFilterRow()]];
        return nextForRedux;
      });

      const filters = buildFiltersFromGroups(nextForRedux);
      if (isDynamicUI && pageSymbol) {
        dispatch(
          reduxActions.setWiseTableAdvancedFilters({
            pageSymbol,
            componentSymbol,
            filters
          })
        );
      } else {
        dispatch(
          reduxActions.setTraderPanelWiseTableAdvancedFilters({
            componentSymbol,
            filters
          })
        );
      }
    },
    [dispatch, isDynamicUI, pageSymbol, componentSymbol]
  );

  const getColumnFilterRowsFromReduxForColumn = useCallback(
    (columnId: string): Array<{
      operator: TWiseTableAdvancedFilterOperator | null;
      value: string;
      connectorAfter?: 'and' | 'or';
    }> => {
      const comparisonOps = ['eq', 'gt', 'lt', 'contains'] as const;
      const result: Array<{
        operator: TWiseTableAdvancedFilterOperator | null;
        value: string;
        connectorAfter?: 'and' | 'or';
      }> = [];
      for (let i = 0; i < savedFilters.length; i++) {
        const f = savedFilters[i];
        if (
          f.columnFieldSymbol === columnId &&
          comparisonOps.includes(f.operator as (typeof comparisonOps)[number])
        ) {
          const next = savedFilters[i + 1];
          const connectorAfter: 'and' | 'or' =
            next?.columnFieldSymbol === '' && (next.operator === 'or' || next.operator === 'and')
              ? next.operator
              : 'and';
          result.push({
            operator: f.operator as TWiseTableAdvancedFilterOperator,
            value: f.value,
            connectorAfter
          });
          if (next?.columnFieldSymbol === '') i++;
        }
      }
      return result;
    },
    [savedFilters]
  );

  const getColumnFilterRowsFromCurrentState = useCallback(
    (columnId: string, groups: IFilterRow[][]): Array<{
      operator: TWiseTableAdvancedFilterOperator | null;
      value: string;
      connectorAfter?: 'and' | 'or';
    }> => {
      for (const group of groups) {
        if (group.length > 0 && group[0].columnFieldSymbol === columnId) {
          return group.map((row) => ({
            operator: row.operator,
            value: row.value,
            connectorAfter: (row.connectorAfter ?? 'and') as 'and' | 'or'
          }));
        }
      }
      return [];
    },
    []
  );

  const getColumnFilterRowsForColumn = useCallback(
    (columnId: string): Array<{
      operator: TWiseTableAdvancedFilterOperator | null;
      value: string;
      connectorAfter?: 'and' | 'or';
    }> => {
      const fromCurrent = getColumnFilterRowsFromCurrentState(columnId, filterRuleGroups);
      if (fromCurrent.length > 0) return fromCurrent;
      return getColumnFilterRowsFromReduxForColumn(columnId);
    },
    [filterRuleGroups, getColumnFilterRowsFromCurrentState, getColumnFilterRowsFromReduxForColumn]
  );

  const addConditionInGroup = useCallback((groupIndex: number, afterRowIndex: number) => {
    setFilterRuleGroups((prev) => {
      const group = prev[groupIndex];
      if (!group || afterRowIndex < 0 || afterRowIndex >= group.length) return prev;
      const sourceRow = group[afterRowIndex];
      const newRow: IFilterRow = {
        ...getEmptyFilterRow(),
        columnFieldSymbol: sourceRow.columnFieldSymbol,
        connectorAfter: 'and'
      };
      const updatedGroup = [...group];
      updatedGroup[afterRowIndex] = { ...sourceRow, connectorAfter: 'and' as const };
      updatedGroup.splice(afterRowIndex + 1, 0, newRow);
      const next = [...prev];
      next[groupIndex] = updatedGroup;
      return next;
    });
  }, []);

  const removeRow = useCallback((groupIndex: number, rowIndex: number) => {
    setFilterRuleGroups((prev) => {
      const next = prev.map((group, gi) =>
        gi === groupIndex ? group.filter((_, ri) => ri !== rowIndex) : group
      );
      const filtered = next.filter((g) => g.length > 0);
      return filtered.length > 0 ? filtered : [[getEmptyFilterRow()]];
    });
  }, []);

  const updateRow = useCallback(
    (groupIndex: number, rowIndex: number, patch: Partial<IFilterRow>) => {
      setFilterRuleGroups((prev) =>
        prev.map((group, gi) => {
          if (gi !== groupIndex) return group;
          const isFirstRow = rowIndex === 0;
          const columnPatch =
            isFirstRow && patch.columnFieldSymbol !== undefined
              ? { columnFieldSymbol: patch.columnFieldSymbol }
              : null;
          return group.map((row, ri) => {
            const updated = ri === rowIndex ? { ...row, ...patch } : row;
            if (columnPatch && ri > 0) {
              return { ...updated, ...columnPatch };
            }
            return updated;
          });
        })
      );
    },
    []
  );

  const buildFiltersFromRows = useCallback((): IWiseTableAdvancedFilter[] => {
    return buildFiltersFromGroups(filterRuleGroups);
  }, [filterRuleGroups]);

  const hasAnyCompleteRow = useMemo(
    () =>
      filterRuleGroups.some((group) =>
        group.some(
          (row) =>
            row.columnFieldSymbol != null &&
            row.operator != null &&
            ['eq', 'gt', 'lt', 'contains'].includes(row.operator) &&
            row.value.trim() !== ''
        )
      ),
    [filterRuleGroups]
  );

  return {
    filterRuleGroups,
    setFilterRuleGroups,
    addRule,
    addConditionInGroup,
    removeRow,
    updateRow,
    applyColumnFilter,
    getColumnFilterRowsForColumn,
    buildFiltersFromRows,
    hasAnyCompleteRow
  };
}
